import {
  DataStyleConfig,
  DisplayConfig,
  FontsConfig,
  ProtectedRoutesConfig,
  RoutesConfig,
  SameAsConfig,
  SchemaConfig,
  SocialSharingConfig,
  StyleConfig,
} from "@/types";
import { home } from "./index";

const baseURL: string = "https://khasiyev.com";

const routes: RoutesConfig = {
  "/": true,
  "/about": true,
  "/work": true,
  "/university": false,
  "/blog": true,
  "/gallery": false,
  "/resume": true,
  "/privacy": true,
};

const display: DisplayConfig = {
  location: false,
  time: true,
  themeSwitcher: true,
};

const protectedRoutes: ProtectedRoutesConfig = {};

import { Fragment_Mono } from "next/font/google";
import localFont from "next/font/local";

// One grotesque for everything, like the reference's single Aeonik.
// General Sans (Fontshare, FFL license, see src/fonts/GENERALSANS-LICENSE.txt)
// is the closest free counterpart; a licensed Aeonik drops in by swapping
// these src paths only.
const heading = localFont({
  src: [
    { path: "../fonts/GeneralSans-Variable.subset.woff2", style: "normal" },
    {
      path: "../fonts/GeneralSans-VariableItalic.subset.woff2",
      style: "italic",
    },
  ],
  variable: "--font-heading",
  weight: "200 700",
  display: "swap",
});

const body = localFont({
  src: [
    { path: "../fonts/GeneralSans-Variable.subset.woff2", style: "normal" },
    {
      path: "../fonts/GeneralSans-VariableItalic.subset.woff2",
      style: "italic",
    },
  ],
  variable: "--font-body",
  weight: "200 700",
  display: "swap",
});

const label = localFont({
  src: [
    { path: "../fonts/GeneralSans-Variable.subset.woff2", style: "normal" },
    {
      path: "../fonts/GeneralSans-VariableItalic.subset.woff2",
      style: "italic",
    },
  ],
  variable: "--font-label",
  weight: "200 700",
  display: "swap",
});

const code = Fragment_Mono({
  variable: "--font-code",
  subsets: ["latin"],
  display: "swap",
  weight: "400",
  // code-only face, never above the fold: keep it off the critical path
  preload: false,
});

// Serif accent: italic spans inside display lines, upright serif intros
// on project and article pages. Never body text.
// PP Editorial New, personal-use license (src/fonts/PPEDITORIALNEW-EULA.pdf)
// — relicense before any commercial use of the site.
const accent = localFont({
  // Only the Ultralight pair ships: every accent rule in custom.css is
  // weight 200, so the Regular/Italic 400 files never render and only
  // added ~72KB of preloaded font weight to the critical path.
  src: [
    {
      path: "../fonts/PPEditorialNew-Ultralight.subset.woff2",
      style: "normal",
      weight: "200",
    },
    {
      path: "../fonts/PPEditorialNew-UltralightItalic.subset.woff2",
      style: "italic",
      weight: "200",
    },
  ],
  variable: "--font-accent",
  display: "swap",
});

const fonts: FontsConfig = {
  heading: heading,
  body: body,
  label: label,
  code: code,
  accent: accent,
};

const style: StyleConfig = {
  theme: "dark",
  neutral: "gray",
  brand: "indigo",
  accent: "red",
  solid: "contrast",
  solidStyle: "flat",
  border: "conservative",
  surface: "filled",
  transition: "micro",
  scaling: "100",
};

const dataStyle: DataStyleConfig = {
  variant: "flat",
  mode: "categorical",
  height: 24,
  axis: {
    stroke: "var(--neutral-alpha-weak)",
  },
  tick: {
    fill: "var(--neutral-on-background-weak)",
    fontSize: 11,
    line: false,
  },
};

const schema: SchemaConfig = {
  logo: "",
  type: "Person",
  name: "Viskhan Khasiyev",
  description: home.description,
  email: "khasiyev.viskhan@gmail.com",
};

const sameAs: SameAsConfig = {
  threads: "",
  linkedin: "https://www.linkedin.com/in/viskhan-khasiyev-45b8822a1",
  discord: "",
};

const socialSharing: SocialSharingConfig = {
  display: false,
  platforms: {
    x: false,
    linkedin: true,
    facebook: false,
    pinterest: false,
    whatsapp: false,
    reddit: false,
    telegram: false,
    email: true,
    copyLink: true,
  },
};

export {
  display,
  routes,
  protectedRoutes,
  baseURL,
  fonts,
  style,
  schema,
  sameAs,
  socialSharing,
  dataStyle,
};
